import { getHours, getMinutes, differenceInMinutes, isValid } from 'date-fns';
import { Shift, DayShift, HourRange, RelativeBreak } from '@/types';
// import { enhancedShiftPositioningService, PositionResult } from './enhanced-shift-positioning-service';

// Temporary placeholder types and service until enhanced-shift-positioning-service is implemented
interface PositionResult {
  left: number;
  width: number;
  top?: number;
}

const enhancedShiftPositioningService = {
  // Add placeholder methods as needed
};

/**
 * Check if a shift ID is a non-DB ID (preview or optimistic temp).
 * These IDs should NOT be used in database queries as they don't exist in the DB
 * and will cause PostgREST 400 errors (expects UUID format).
 */
export const isPlanningPreviewId = (shiftId: string): boolean => {
  return shiftId.startsWith('paste-preview-') ||
         shiftId.startsWith('planning-preview-') ||
         shiftId.startsWith('template-preview-') ||
         shiftId.startsWith('temp-paste-') ||
         shiftId.startsWith('temp-template-');
};

// Helper function to detect overlaps between shifts
export const doShiftsOverlap = (shift1: Shift | DayShift, shift2: Shift | DayShift): boolean => {
  return (
    shift1.startTime < shift2.endTime &&
    shift2.startTime < shift1.endTime
  );
};

// Helper function to assign shifts to rows for display
export interface ShiftRow {
  rowIndex: number;
  shifts: (Shift | DayShift)[];
}

// Optimized row organization with O(n log n) complexity
export const organizeShiftsIntoRows = (shifts: (Shift | DayShift)[]): ShiftRow[] => {
  if (shifts.length === 0) return [];

  const rows: ShiftRow[] = [];
  // Track the end time of the last shift in each row for faster overlap detection
  const rowEndTimes: Date[] = [];

  // Sort shifts by start time once - O(n log n)
  const sortedShifts = [...shifts].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  for (const shift of sortedShifts) {
    let assignedToRow = false;

    // Find the first row where this shift can fit - O(k) where k is number of rows
    // In practice, k is much smaller than n for most scheduling scenarios
    for (let i = 0; i < rows.length; i++) {
      // Quick check: if shift starts after the row's last end time, it fits
      if (shift.startTime >= rowEndTimes[i]) {
        rows[i].shifts.push(shift);
        rowEndTimes[i] = shift.endTime;
        assignedToRow = true;
        break;
      }
    }

    // If no suitable row found, create a new one
    if (!assignedToRow) {
      rows.push({
        rowIndex: rows.length,
        shifts: [shift]
      });
      rowEndTimes.push(shift.endTime);
    }
  }

  return rows;
};

// Helper function to format duration in hours and minutes
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours > 0 ? `${hours}h ` : ''}${mins > 0 ? `${mins}m` : ''}`;
};

// **PERFORMANCE OPTIMIZATION**: Position calculation now handled by EnhancedShiftPositioningService
// Legacy cache functions maintained for backward compatibility

// Get cache statistics (delegates to enhanced service)
export const getPositionCacheStats = () => {
  const stats = enhancedShiftPositioningService.getCacheStatistics();
  return {
    size: stats.positionCacheSize,
    hitCount: stats.positionHitCount,
    missCount: stats.positionMissCount,
    hitRate: stats.positionHitRate
  };
};

// Helper function to get position and width for shifts and hour ranges in pixels
// **MIGRATED**: This function has been moved to positioning-compatibility-layer.ts
// Components should import directly from there or use the enhanced service
// Removed export to prevent conflicting star exports


// Validate breaks to ensure they don't extend beyond shift duration or overlap (from DayShiftPanel.tsx)
export const validateBreaks = (breaks: RelativeBreak[], shiftDuration: number): boolean => {
  // Sort breaks by start time for overlap checking
  const sortedBreaks = [...breaks].sort((a, b) => a.startMinute - b.startMinute);
  
  for (let i = 0; i < sortedBreaks.length; i++) {
    const breakItem = sortedBreaks[i];
    
    // Check if break starts within shift duration
    if (breakItem.startMinute < 0 || breakItem.startMinute >= shiftDuration) {
      // toast.error(`Break #${i + 1} starts outside the shift duration`); // Removed toast here, will handle in component
      return false;
    }
    
    // Check if break ends within shift duration
    if (breakItem.startMinute + breakItem.durationMinutes > shiftDuration) {
      // toast.error(`Break #${i + 1} extends beyond the shift duration`); // Removed toast here, will handle in component
      return false;
    }
    
    // Check for overlap with next break
    if (i < sortedBreaks.length - 1) {
      const nextBreak = sortedBreaks[i + 1];
      if (breakItem.startMinute + breakItem.durationMinutes > nextBreak.startMinute) {
        // toast.error(`Break #${i + 1} overlaps with the next break`); // Removed toast here, will handle in component
        return false;
      }
    }
  }
  
  return true;
};

// Check if an employee is available for a specific shift time slot
export interface EmployeeAvailability {
  employeeId: string;
  isAvailable: boolean;
  conflictingShifts?: {
    id: string;
    startTime: Date;
    endTime: Date;
    title?: string;
  }[];
}

// Cached empty result object to prevent unnecessary allocations
const EMPTY_AVAILABILITY: EmployeeAvailability = {
  employeeId: '',
  isAvailable: true,
  conflictingShifts: []
};

// Empty conflicting shifts array (reused to prevent allocations)
const EMPTY_CONFLICTING_SHIFTS: readonly [] = [];

export const isEmployeeAvailableForShift = (
  employeeId: string,
  proposedStartTime: Date,
  proposedEndTime: Date,
  existingShifts: (Shift | DayShift)[],
  excludeShiftId?: string
): EmployeeAvailability => {
  // Fast path: Early return when no shifts to check
  if (existingShifts.length === 0) {
    return { ...EMPTY_AVAILABILITY, employeeId };
  }

  // Build conflicting shifts array using for-loop for early exit
  const conflictingShifts: {
    id: string;
    startTime: Date;
    endTime: Date;
    title?: string;
  }[] = [];

  const proposedStart = proposedStartTime.getTime();
  const proposedEnd = proposedEndTime.getTime();

  // Single pass through shifts - O(n) with early exit capability
  for (let i = 0; i < existingShifts.length; i++) {
    const shift = existingShifts[i];

    // Skip if not this employee or is the excluded shift
    if (shift.employeeId !== employeeId || shift.id === excludeShiftId) {
      continue;
    }

    // Check for overlap using cached timestamps for comparison
    const shiftStart = shift.startTime.getTime();
    const shiftEnd = shift.endTime.getTime();

    // Overlap condition: (StartA < EndB) && (EndA > StartB)
    if (proposedStart < shiftEnd && proposedEnd > shiftStart) {
      conflictingShifts.push({
        id: shift.id,
        startTime: shift.startTime,
        endTime: shift.endTime,
        title: shift.title
      });

      // Early exit for performance - we only need to know IF there's a conflict
      // Collecting ALL conflicts creates unnecessary work for drag previews
      break;
    }
  }

  // Return a memoized result object to prevent unnecessary allocations
  return {
    employeeId,
    isAvailable: conflictingShifts.length === 0,
    conflictingShifts: conflictingShifts.length > 0 ? conflictingShifts : EMPTY_CONFLICTING_SHIFTS as any
  };
};

// Check availability for multiple employees at once
export const checkMultipleEmployeeAvailability = (
  employees: { id: string; name: string }[],
  proposedStartTime: Date,
  proposedEndTime: Date,
  existingShifts: (Shift | DayShift)[],
  excludeShiftId?: string
): Map<string, EmployeeAvailability> => {
  const availabilityMap = new Map<string, EmployeeAvailability>();

  employees.forEach(employee => {
    const availability = isEmployeeAvailableForShift(
      employee.id,
      proposedStartTime,
      proposedEndTime,
      existingShifts,
      excludeShiftId
    );
    availabilityMap.set(employee.id, availability);
  });

  return availabilityMap;
};

// Result type for filtering paste preview shifts by conflict status
export interface PasteConflictFilterResult {
  valid: DayShift[];
  conflicting: DayShift[];
}

/**
 * Filter paste preview shifts, keeping only those without conflicts.
 * Uses the hasConflict property already set during preview generation.
 *
 * This function is used when saving paste operations to ensure conflicting
 * shifts are not inserted into the database.
 *
 * @param pasteShifts - Array of DayShift objects from paste preview (with hasConflict set)
 * @returns Object with valid (non-conflicting) and conflicting shift arrays
 */
export const filterNonConflictingPasteShifts = (
  pasteShifts: DayShift[]
): PasteConflictFilterResult => {
  const valid: DayShift[] = [];
  const conflicting: DayShift[] = [];

  for (const shift of pasteShifts) {
    if (shift.hasConflict) {
      conflicting.push(shift);
    } else {
      valid.push(shift);
    }
  }

  return { valid, conflicting };
};
